import type { APInvoice, SearchDocumentResult } from '@erp/rpc';
import { APInvoiceStatus } from '@erp/shared-types';
import { T, useGT, Var, Num, Plural } from 'gt-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  FileTextIcon,
  BuildingsIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  WarningIcon,
  CheckCircleIcon,
  XCircleIcon,
  CubeIcon,
  InfoIcon,
  PencilIcon,
  CheckIcon,
  UserIcon,
} from '@phosphor-icons/react';
import {
  AlertContainer as Alert,
  AlertDescription,
} from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatDateOnlyString, parseDateOnly } from '@/utils/dateUtils';
import { useMemo, useState } from 'react';
import { RerunWorkflowStepDropdown } from '@/components/workflows/RerunWorkflowStepDropdown';
import useAuth from '@/api/auth/useAuth';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin as checkIsAdmin } from '@/utils/user';
import {
  useCreateEntityAssociation,
  useEntityAssociationsList,
} from '@/api/rpc/hooks/core/useEntityAssociations';
import {
  SystemEntity,
  EntityAssociationType,
  ReferenceEntityType,
} from '@erp/shared-types';
import { SystemEntityInline } from '@/components/system-entities';
import { SupplierAutocomplete } from '@/components/company/SupplierAutocomplete';
import { DocumentAutocomplete } from '@/components/accounts-payable/DocumentAutocomplete';
import { useUpdateAPInvoice } from '@/api/rpc/hooks/accounts-payable/useUpdateAPInvoice';
import { DatePicker } from '@/components/common/date-pickers/date-picker';
import { PillInput } from '@/components/common/pills/PillInput';
import { PillList } from '@/components/common/pills/PillList';
import { useUsersByIds } from '@/api/core/users/useUsersByIds';
import { useFeatureFlag } from '@/integrations/feature-flags/feature-flags';
import { FEATURE_FLAGS } from '@erp/shared-types';
import { ExternalReferencesList } from '@/components/system-entities/external-references-list';
import {
  getMatchDisplayState,
  DISPLAY_STATE_BADGE_CLASSES,
} from '@/components/accounts-payable/shared/match-display-state';
// AP Invoices Demo (delete me) — inline "why did this match?" evidence chip;
// also renders reference IDs with the real linked-entity renderer in demo mode.
import { MatchProvenanceChip } from '@/components/accounts-payable/shared/match-provenance-chip';
import { useApSupplierProvenance } from '@/demo/ap/provenance';
import { isApDemoMode } from '@/demo/ap/demo-mode';

import { format } from 'date-fns';

// Helper function to format currency
const formatCurrencyValue = (
  value: string | undefined | null,
  currency = 'USD',
): string => {
  if (!value) return 'N/A';

  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;

  return numeric.toLocaleString(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });
};

// Helper function to generate potential errors
const generatePotentialErrors = (
  invoice: APInvoice,
): { type: 'error' | 'warning'; message: string }[] => {
  const issues: { type: 'error' | 'warning'; message: string }[] = [];

  // Check for missing required fields (errors)
  if (!invoice.invoiceNumber) {
    issues.push({ type: 'error', message: 'Missing invoice number' });
  }

  const totalAmount = Number(invoice.totalAmount);
  if (!invoice.totalAmount || Number.isNaN(totalAmount) || totalAmount <= 0) {
    issues.push({ type: 'error', message: 'Invalid total amount' });
  }

  // Check for voided invoices
  if (invoice.isVoided) {
    issues.push({ type: 'warning', message: 'Invoice has been voided' });
  }

  return issues;
};

const SYSTEM_ENTITY_TO_REFERENCE_ENTITY_TYPE: Partial<
  Record<SystemEntity, ReferenceEntityType>
> = {
  [SystemEntity.INTERNAL_PURCHASE_ORDER]: ReferenceEntityType.PurchaseOrder,
  [SystemEntity.AR_SALES_ORDER]: ReferenceEntityType.SalesOrder,
  [SystemEntity.SCM_SHIPMENT]: ReferenceEntityType.Shipment,
};

interface InvoiceMetadataProps {
  invoice: APInvoice;
}

function renderGLAccountSummary(
  label: string,
  glAccount: APInvoice['glAccount'] | null | undefined,
) {
  if (!glAccount) return null;

  return (
    <div className="space-y-0.5">
      <div>
        <span className="text-muted-foreground">{label}:</span>{' '}
        {glAccount.accountCode} - {glAccount.accountName}
      </div>
      {glAccount.accountType && (
        <div>
          <span className="text-muted-foreground">
            <T>Type:</T>
          </span>{' '}
          {glAccount.accountType}
        </div>
      )}
      {glAccount.accountDescription && (
        <div>
          <span className="text-muted-foreground">
            <T>Description:</T>
          </span>{' '}
          {glAccount.accountDescription}
        </div>
      )}
    </div>
  );
}

export function InvoiceMetadata({ invoice }: InvoiceMetadataProps) {
  const gt = useGT();
  const { currentUser } = useAuth();
  const { viewMode } = useViewMode();
  const userIsAdmin = checkIsAdmin(currentUser, viewMode);
  const { enabled: extRefsEnabled } = useFeatureFlag(
    FEATURE_FLAGS['ext-system-references-enabled'],
    false,
  );

  const issues = generatePotentialErrors(invoice);
  const errors = issues.filter((issue) => issue.type === 'error');
  const warnings = issues.filter((issue) => issue.type === 'warning');
  const updateInvoice = useUpdateAPInvoice();
  // AP Invoices Demo (delete me) — memory-bank provenance for the supplier match.
  const supplierProvenance = useApSupplierProvenance(invoice.id);

  const matchSummary = invoice.lineItemMatchSummary;
  const displayState = getMatchDisplayState(invoice.status, matchSummary);
  const isAcceptedOrFinalized =
    displayState === 'ACCEPTED' || displayState === 'FINALIZED';
  const isFullyMatched =
    matchSummary != null &&
    matchSummary.total > 0 &&
    matchSummary.matched === matchSummary.total;

  // Fetch associated POs
  const poAssociationsQuery = useEntityAssociationsList({
    entityId: invoice.id,
    entityType: SystemEntity.AP_INVOICE,
    associationType: EntityAssociationType.AP_INVOICE_MATCHING,
  });

  const apAssociations = useMemo(
    () => poAssociationsQuery.data?.associations || [],
    [poAssociationsQuery.data?.associations],
  );
  const createAssociation = useCreateEntityAssociation();

  // Get IDs of associated documents (POs, SOs, and Shipments)
  const associatedDocumentIds = useMemo(
    () =>
      apAssociations
        .filter(
          (assoc) =>
            assoc.entity.entityType === SystemEntity.INTERNAL_PURCHASE_ORDER ||
            assoc.entity.entityType === SystemEntity.AR_SALES_ORDER ||
            assoc.entity.entityType === SystemEntity.SCM_SHIPMENT,
        )
        .map((assoc) => assoc.entity.entityId),
    [apAssociations],
  );

  const linkedRefEntities = useMemo(
    () =>
      apAssociations.flatMap((assoc) => {
        const refEntityType =
          SYSTEM_ENTITY_TO_REFERENCE_ENTITY_TYPE[assoc.entity.entityType];
        if (!refEntityType) return [];
        return [{ entityType: refEntityType, entityId: assoc.entity.entityId }];
      }),
    [apAssociations],
  );

  const nonLinkedRefEntities = useMemo(
    () => [{ entityType: ReferenceEntityType.Invoice, entityId: invoice.id }],
    [invoice.id],
  );

  // Allow editing for DRAFT and MATCHED invoices
  const isEditable =
    invoice.status === APInvoiceStatus.DRAFT ||
    invoice.status === APInvoiceStatus.MATCHED;

  const [isEditingReferenceIds, setIsEditingReferenceIds] = useState(false);

  const approvedByUserId = invoice.approvedByUserId ?? null;
  const approvedByUsersQuery = useUsersByIds(
    approvedByUserId ? [approvedByUserId] : [],
    Boolean(approvedByUserId),
  );
  const approvedByUserName = approvedByUserId
    ? (approvedByUsersQuery.data?.[0]?.name ??
      `User ${approvedByUserId.slice(0, 8)}...`)
    : null;
  const approvedAtLabel = invoice.approvedAt
    ? format(new Date(invoice.approvedAt), 'MMM d, yyyy h:mm a')
    : null;
  const supplierGLAccounts = invoice.supplier?.glAccounts;
  const hasSupplierGLAccounts = Boolean(
    supplierGLAccounts?.expense ||
    supplierGLAccounts?.ap ||
    supplierGLAccounts?.po,
  );

  const handleAssociateDocument = async (document: SearchDocumentResult) => {
    let targetEntityType: SystemEntity;
    switch (document.documentType) {
      case 'PURCHASE_ORDER':
        targetEntityType = SystemEntity.INTERNAL_PURCHASE_ORDER;
        break;
      case 'SALES_ORDER':
        targetEntityType = SystemEntity.AR_SALES_ORDER;
        break;
      case 'SHIPMENT':
        targetEntityType = SystemEntity.SCM_SHIPMENT;
        break;
    }

    await createAssociation.mutateAsync({
      sourceEntityType: SystemEntity.AP_INVOICE,
      sourceEntityId: invoice.id,
      targetEntityType,
      targetEntityId: document.id,
      associationType: EntityAssociationType.AP_INVOICE_MATCHING,
    });
  };

  // Check if scheduled date is tentative (not finalized)
  const isScheduledDateTentative =
    invoice.scheduledPaymentDate &&
    invoice.status !== APInvoiceStatus.FINALIZED;

  const handleSupplierChange = (supplierId: string | undefined) => {
    updateInvoice.mutate({
      id: invoice.id,
      supplierId: supplierId || null,
    });
  };

  const handleReferenceIdsUpdate = (referenceIds: string[]) => {
    updateInvoice.mutate({
      id: invoice.id,
      referenceIds: referenceIds.length > 0 ? referenceIds : null,
    });
  };
  const handleInvoiceDateChange = (date: Date | undefined) => {
    if (date) {
      updateInvoice.mutate({
        id: invoice.id,
        invoiceDate: format(date, 'yyyy-MM-dd'),
      });
    }
  };

  const handleDueDateChange = (date: Date | undefined) => {
    updateInvoice.mutate({
      id: invoice.id,
      dueDate: date ? format(date, 'yyyy-MM-dd') : null,
    });
  };

  const handlePreferredPayDateChange = (date: Date | undefined) => {
    updateInvoice.mutate({
      id: invoice.id,
      preferredPayDate: date ? format(date, 'yyyy-MM-dd') : null,
    });
  };

  const handleScheduledPaymentDateChange = (date: Date | undefined) => {
    const newISO = date ? date.toISOString() : null;
    const existingISO = invoice.scheduledPaymentDate ?? null;
    // Don't persist if the date hasn't changed (comparing date portion only)
    if (
      newISO?.slice(0, 10) === existingISO?.slice(0, 10) &&
      !(newISO === null && existingISO !== null) &&
      !(newISO !== null && existingISO === null)
    ) {
      return;
    }
    updateInvoice.mutate({
      id: invoice.id,
      scheduledPaymentDate: newISO,
      scheduledPaymentExplanation: date ? 'Manually set by user' : null,
    });
  };

  // Parse date strings to Date objects for the DatePicker
  // DATEONLY fields (invoiceDate, dueDate, preferredPayDate) use parseDateOnly
  // DATE/timestamp fields (scheduledPaymentDate) use Date constructor
  const invoiceDateValue = parseDateOnly(invoice.invoiceDate) ?? undefined;
  const dueDateValue = invoice.dueDate
    ? (parseDateOnly(invoice.dueDate) ?? undefined)
    : undefined;
  const preferredPayDateValue = invoice.preferredPayDate
    ? (parseDateOnly(invoice.preferredPayDate) ?? undefined)
    : undefined;
  // scheduledPaymentDate is a full ISO timestamp, not DATEONLY
  const scheduledPaymentDateValue = invoice.scheduledPaymentDate
    ? new Date(invoice.scheduledPaymentDate)
    : undefined;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileTextIcon className="h-4 w-4" />
            <T>
              Vendor Invoice: <Var>{invoice.invoiceNumber || 'N/A'}</Var>
            </T>
          </CardTitle>
          <div className="flex items-center gap-2">
            <RerunWorkflowStepDropdown
              entityType={SystemEntity.AP_INVOICE}
              entityId={invoice.id}
              isAdmin={userIsAdmin}
            />
            <div className="flex items-center gap-2">
              <Badge className={DISPLAY_STATE_BADGE_CLASSES[displayState]}>
                {displayState}
                {matchSummary && matchSummary.total > 0
                  ? ` (${(matchSummary.confirmed ?? 0) + (matchSummary.tentative ?? 0)}/${matchSummary.total})`
                  : ''}
              </Badge>
              {matchSummary && matchSummary.total > 0 && (
                <div className="flex h-1.5 w-12 rounded-full overflow-hidden bg-muted">
                  {isAcceptedOrFinalized ? (
                    <>
                      {matchSummary.confirmed + (matchSummary.tentative ?? 0) >
                        0 && (
                        <div
                          className="bg-blue-300"
                          style={{
                            width: `${((matchSummary.confirmed + (matchSummary.tentative ?? 0)) / matchSummary.total) * 100}%`,
                          }}
                        />
                      )}
                      {(matchSummary.unmatched ?? 0) > 0 && (
                        <div
                          className="bg-gray-300"
                          style={{
                            width: `${((matchSummary.unmatched ?? 0) / matchSummary.total) * 100}%`,
                          }}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {matchSummary.confirmed > 0 && (
                        <div
                          className="bg-green-500"
                          style={{
                            width: `${(matchSummary.confirmed / matchSummary.total) * 100}%`,
                          }}
                        />
                      )}
                      {(matchSummary.tentative ?? 0) > 0 && (
                        <div
                          className="bg-yellow-400"
                          style={{
                            width: `${((matchSummary.tentative ?? 0) / matchSummary.total) * 100}%`,
                          }}
                        />
                      )}
                      {(matchSummary.unmatched ?? 0) > 0 && (
                        <div
                          className="bg-red-400"
                          style={{
                            width: `${((matchSummary.unmatched ?? 0) / matchSummary.total) * 100}%`,
                          }}
                        />
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {invoice.paymentHold && (
          <Alert className="border-amber-500 bg-amber-50 py-3">
            <WarningIcon className="h-5 w-5 text-amber-600" />
            <AlertDescription>
              <div className="font-semibold text-amber-800">
                <T>Payment on Hold</T>
              </div>
              {invoice.paymentHoldReason && (
                <div className="text-sm text-amber-700 mt-1">
                  <T>
                    Reason: <Var>{invoice.paymentHoldReason}</Var>
                  </T>
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}

        {isEditable && (
          <DocumentAutocomplete
            invoiceId={invoice.id}
            supplierId={invoice.supplierId || undefined}
            invoiceDate={invoice.invoiceDate}
            invoiceAmount={invoice.totalAmount}
            currency={invoice.currency || 'USD'}
            excludeDocumentIds={associatedDocumentIds}
            onSelect={(document) => {
              if (createAssociation.isPending) return;
              void handleAssociateDocument(document);
            }}
            placeholder={gt('Link entities...')}
          />
        )}

        {/* Main Invoice Details */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <BuildingsIcon className="h-3 w-3" />
              <T>Supplier</T>
            </div>
            {isEditable ? (
              <SupplierAutocomplete
                supplierId={invoice.supplierId || undefined}
                onSelect={handleSupplierChange}
                className="w-full"
              />
            ) : invoice.supplierId ? (
              <SystemEntityInline
                entityType={SystemEntity.AP_SUPPLIER}
                entityId={invoice.supplierId}
              />
            ) : (
              <div className="text-xs text-muted-foreground">-</div>
            )}
            {/* AP Invoices Demo (delete me) */}
            {supplierProvenance && (
              <div className="pt-1">
                <MatchProvenanceChip provenance={supplierProvenance} />
              </div>
            )}
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <FileTextIcon className="h-3 w-3" />
                <T>Reference IDs</T>
              </div>
              {!extRefsEnabled && isEditable && !isApDemoMode() && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => setIsEditingReferenceIds((v) => !v)}
                  aria-label={
                    isEditingReferenceIds
                      ? gt('Done editing reference IDs')
                      : gt('Edit reference IDs')
                  }
                >
                  {isEditingReferenceIds ? (
                    <CheckIcon className="h-3 w-3" />
                  ) : (
                    <PencilIcon className="h-3 w-3" />
                  )}
                </Button>
              )}
            </div>
            {/* AP Invoices Demo (delete me) — render references as real linked
                PO/SO/Shipment entities instead of plain text pills. */}
            {isApDemoMode() && associatedDocumentIds.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {apAssociations
                  .filter(
                    (assoc) =>
                      assoc.entity.entityType ===
                        SystemEntity.INTERNAL_PURCHASE_ORDER ||
                      assoc.entity.entityType === SystemEntity.AR_SALES_ORDER ||
                      assoc.entity.entityType === SystemEntity.SCM_SHIPMENT,
                  )
                  .map((assoc) => (
                    <SystemEntityInline
                      key={assoc.entity.entityId}
                      entityType={assoc.entity.entityType}
                      entityId={assoc.entity.entityId}
                    />
                  ))}
              </div>
            ) : extRefsEnabled ? (
              <ExternalReferencesList
                entities={linkedRefEntities}
                nonLinkedEntities={nonLinkedRefEntities}
                fallback={
                  invoice.referenceIds && invoice.referenceIds.length > 0 ? (
                    <PillList values={invoice.referenceIds} />
                  ) : (
                    <div className="text-sm text-muted-foreground">—</div>
                  )
                }
              />
            ) : isEditable && isEditingReferenceIds ? (
              <PillInput
                values={invoice.referenceIds ?? []}
                onChange={handleReferenceIdsUpdate}
                placeholder="Add reference ID..."
              />
            ) : invoice.referenceIds && invoice.referenceIds.length > 0 ? (
              <PillList values={invoice.referenceIds} />
            ) : (
              <div className="text-sm text-muted-foreground">—</div>
            )}
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CurrencyDollarIcon className="h-3 w-3" />
              <T>Total Amount</T>
            </div>
            <div className="text-lg font-bold text-green-600">
              {formatCurrencyValue(invoice.totalAmount, invoice.currency)}
            </div>
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CalendarIcon className="h-3 w-3" />
              <T>Invoice Date</T>
            </div>
            {isEditable ? (
              <DatePicker
                value={invoiceDateValue}
                onSelect={handleInvoiceDateChange}
                placeholder={gt('Select date')}
              />
            ) : (
              <div className="font-medium text-sm">
                {formatDateOnlyString(invoice.invoiceDate)}
              </div>
            )}
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CalendarIcon className="h-3 w-3" />
              <T>Due Date</T>
            </div>
            {isEditable ? (
              <DatePicker
                value={dueDateValue}
                onSelect={handleDueDateChange}
                placeholder={gt('Select due date')}
              />
            ) : (
              <div className="font-medium text-sm">
                {invoice.dueDate
                  ? formatDateOnlyString(invoice.dueDate)
                  : gt('Not specified')}
              </div>
            )}
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CalendarIcon className="h-3 w-3" />
              <T>Preferred Pay Date</T>
            </div>
            {isEditable ? (
              <DatePicker
                value={preferredPayDateValue}
                onSelect={handlePreferredPayDateChange}
                placeholder={gt('Select preferred date')}
              />
            ) : (
              <div className="font-medium text-sm">
                {invoice.preferredPayDate
                  ? formatDateOnlyString(invoice.preferredPayDate)
                  : gt('Not specified')}
              </div>
            )}
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CalendarIcon className="h-3 w-3" />
              <T>Scheduled Pay Date</T>
              {invoice.scheduledPaymentExplanation && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex cursor-help">
                      <InfoIcon className="h-3 w-3 text-muted-foreground" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-sm">
                    <p className="font-medium mb-1">
                      <T>Calculation Details</T>
                    </p>
                    <pre className="whitespace-pre-wrap text-xs">
                      {invoice.scheduledPaymentExplanation}
                    </pre>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            {isEditable ? (
              <DatePicker
                value={scheduledPaymentDateValue}
                onSelect={handleScheduledPaymentDateChange}
                placeholder={gt('Select scheduled date')}
              />
            ) : (
              <div className="font-medium text-sm flex items-center gap-1">
                {invoice.scheduledPaymentDate
                  ? formatDateOnlyString(invoice.scheduledPaymentDate)
                  : gt('Not scheduled')}
              </div>
            )}
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <UserIcon className="h-3 w-3" />
              <T>Accepted</T>
            </div>
            {approvedAtLabel ? (
              <div className="font-medium text-sm">
                {approvedByUserName
                  ? `${approvedByUserName} | ${approvedAtLabel}`
                  : approvedAtLabel}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                <T>Not accepted</T>
              </div>
            )}
          </div>
        </div>

        {/* Error and Warning Display */}
        {(errors.length > 0 || warnings.length > 0) && (
          <div className="space-y-2">
            {errors.length > 0 && (
              <Alert className="border-red-200 bg-red-50 py-2">
                <XCircleIcon className="h-3 w-3 text-red-600" />
                <AlertDescription>
                  <div className="font-medium text-red-800 text-xs mb-1">
                    <T>
                      <Num>{errors.length}</Num>{' '}
                      <Plural n={errors.length} one="Error" other="Errors" />{' '}
                      Found
                    </T>
                  </div>
                  <ul className="text-xs text-red-700 space-y-0.5">
                    {errors.map((error, index) => (
                      <li key={index} className="flex items-center gap-1.5">
                        <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                        {error.message}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {warnings.length > 0 && (
              <Alert className="border-amber-200 bg-amber-50 py-2">
                <WarningIcon className="h-3 w-3 text-amber-600" />
                <AlertDescription>
                  <div className="font-medium text-amber-800 text-xs mb-1">
                    <T>
                      <Num>{warnings.length}</Num>{' '}
                      <Plural
                        n={warnings.length}
                        one="Warning"
                        other="Warnings"
                      />
                    </T>
                  </div>
                  <ul className="text-xs text-amber-700 space-y-0.5">
                    {warnings.map((warning, index) => (
                      <li key={index} className="flex items-center gap-1.5">
                        <span className="w-1 h-1 bg-amber-600 rounded-full"></span>
                        {warning.message}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {errors.length === 0 && warnings.length === 0 && (
              <Alert className="border-green-200 bg-green-50 py-2">
                <CheckCircleIcon className="h-3 w-3 text-green-600" />
                <AlertDescription>
                  <div className="font-medium text-green-800 text-xs">
                    <T>No issues detected - Invoice is valid for processing</T>
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* Origin System Sync Status (e.g., Famous Voucher) */}
        {invoice.originSyncStatus &&
          invoice.originSystem === 'famous' &&
          invoice.originEntity === 'voucher' && (
            <div className="border-t pt-3 space-y-2">
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-muted-foreground">
                  <T>Famous Voucher Status</T>
                </div>
                <div className="flex items-center gap-2">
                  {invoice.originSyncStatus === 'success' ? (
                    <>
                      <CheckCircleIcon className="h-3 w-3 text-green-600" />
                      <Badge variant="outline" className="text-xs">
                        <T>Created</T>
                      </Badge>
                    </>
                  ) : invoice.originSyncStatus === 'pending' ? (
                    <>
                      <CubeIcon className="h-3 w-3 text-blue-600" />
                      <Badge variant="outline" className="text-xs">
                        <T>Pending</T>
                      </Badge>
                    </>
                  ) : invoice.originSyncStatus === 'failed' ? (
                    <>
                      <XCircleIcon className="h-3 w-3 text-red-600" />
                      <Badge variant="outline" className="text-xs">
                        <T>Failed</T>
                      </Badge>
                    </>
                  ) : null}
                </div>
              </div>
              {invoice.originId && (
                <div className="space-y-0.5">
                  <div className="text-xs font-medium text-muted-foreground">
                    <T>Entry Number</T>
                  </div>
                  <div className="text-xs font-mono">{invoice.originId}</div>
                </div>
              )}
              {invoice.originMetadata?.syncedAt &&
              typeof invoice.originMetadata.syncedAt === 'string' ? (
                <div className="space-y-0.5">
                  <div className="text-xs font-medium text-muted-foreground">
                    <T>Synced At</T>
                  </div>
                  <div className="text-xs">
                    {new Date(invoice.originMetadata.syncedAt).toLocaleString()}
                  </div>
                </div>
              ) : null}
              {invoice.originSyncErrorMessage && (
                <div className="space-y-0.5">
                  <div className="text-xs font-medium text-red-600">
                    <T>Error Message</T>
                  </div>
                  <div className="text-xs text-red-600">
                    {invoice.originSyncErrorMessage}
                  </div>
                </div>
              )}
            </div>
          )}

        {/* Additional Details */}
        {(invoice.paymentTerms ||
          invoice.supplier ||
          invoice.glAccount ||
          hasSupplierGLAccounts) && (
          <div className="border-t pt-3 space-y-3">
            {invoice.paymentTerms && (
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-muted-foreground">
                  <T>Payment Terms</T>
                </div>
                <div className="text-xs">{invoice.paymentTerms}</div>
              </div>
            )}
            {invoice.supplier && (
              <div className="space-y-1.5">
                <div className="text-xs font-medium text-muted-foreground">
                  <T>Supplier Details</T>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  <div>
                    <span className="text-muted-foreground">
                      <T>Name:</T>
                    </span>{' '}
                    {invoice.supplier.name}
                  </div>
                  <div>
                    <span className="text-muted-foreground">
                      <T>Status:</T>
                    </span>{' '}
                    {invoice.supplier.status}
                  </div>
                  {invoice.supplier.contactName && (
                    <div>
                      <span className="text-muted-foreground">
                        <T>Contact:</T>
                      </span>{' '}
                      {invoice.supplier.contactName}
                    </div>
                  )}
                  {invoice.supplier.email && (
                    <div>
                      <span className="text-muted-foreground">
                        <T>Email:</T>
                      </span>{' '}
                      {invoice.supplier.email}
                    </div>
                  )}
                  {invoice.supplier.phone && (
                    <div>
                      <span className="text-muted-foreground">
                        <T>Phone:</T>
                      </span>{' '}
                      {invoice.supplier.phone}
                    </div>
                  )}
                  {invoice.supplier.paymentTerms && (
                    <div>
                      <span className="text-muted-foreground">
                        <T>Supplier Terms:</T>
                      </span>{' '}
                      {invoice.supplier.paymentTerms}
                    </div>
                  )}
                  {invoice.supplier.defaultPaymentMethod && (
                    <div>
                      <span className="text-muted-foreground">
                        <T>Payment Type:</T>
                      </span>{' '}
                      {invoice.supplier.defaultPaymentMethod.toUpperCase()}
                    </div>
                  )}
                </div>
                {invoice.supplier.notes &&
                  invoice.supplier.notes.length > 0 && (
                    <div className="mt-1.5">
                      <span className="text-muted-foreground text-xs">
                        <T>Notes:</T>
                      </span>
                      <ul className="text-xs mt-0.5 space-y-0.5">
                        {invoice.supplier.notes.map((note, idx) => (
                          <li key={idx} className="flex gap-1.5">
                            <span className="text-muted-foreground">-</span>
                            {note}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                {hasSupplierGLAccounts && (
                  <div className="mt-1.5 space-y-1.5">
                    <div className="text-xs font-medium text-muted-foreground">
                      <T>Supplier GL Accounts</T>
                    </div>
                    <div className="grid grid-cols-1 gap-y-1 text-xs">
                      {renderGLAccountSummary(
                        gt('Expense'),
                        supplierGLAccounts?.expense,
                      )}
                      {renderGLAccountSummary(gt('AP'), supplierGLAccounts?.ap)}
                      {renderGLAccountSummary(gt('PO'), supplierGLAccounts?.po)}
                    </div>
                  </div>
                )}
              </div>
            )}
            {invoice.glAccount && (
              <div className="space-y-1.5">
                <div className="text-xs font-medium text-muted-foreground">
                  <T>GL Account</T>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  <div>
                    <span className="text-muted-foreground">
                      <T>Code:</T>
                    </span>{' '}
                    {invoice.glAccount.accountCode}
                  </div>
                  <div>
                    <span className="text-muted-foreground">
                      <T>Name:</T>
                    </span>{' '}
                    {invoice.glAccount.accountName}
                  </div>
                  {invoice.glAccount.accountType && (
                    <div>
                      <span className="text-muted-foreground">
                        <T>Type:</T>
                      </span>{' '}
                      {invoice.glAccount.accountType}
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">
                      <T>Control:</T>
                    </span>{' '}
                    {invoice.glAccount.isControlAccount ? gt('Yes') : gt('No')}
                  </div>
                  {invoice.glAccount.accountDescription && (
                    <div className="col-span-2">
                      <span className="text-muted-foreground">
                        <T>Description:</T>
                      </span>{' '}
                      {invoice.glAccount.accountDescription}
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* invoice.notes && (
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-muted-foreground">
                  Notes
                </div>
                <div className="text-xs">{invoice.notes}</div>
              </div>
            )*/}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
